import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { showToast } from '../../store/uiSlice';
import { adminApi } from '../../services/api';

const Settings = () => {
  const dispatch = useDispatch();
  const [settings, setSettings] = useState({
    siteName: 'Groxion',
    siteVersion: '6.1',
    maintenanceMode: false,
    registrationEnabled: true,
    minDeposit: 50,
    maxDeposit: 1000000,
    minWithdrawal: 50,
    maxWithdrawalPerDay: 25000,
    defaultLeverage: 50,
    maxLeverage: 500,
    tradingFee: 0,
    withdrawalFee: 0,
    supportEmail: 'support@groxion.com',
    supportPhone: '+1 888 555 0123',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('general');

  useEffect(() => { fetchSettings(); }, []);

  const fetchSettings = async () => {
    try {
      const response = await adminApi.get('/admin/settings');
      setSettings(response.data);
    } catch (error) { console.error('Failed to fetch settings:', error); }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSettings(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSave = async () => {
    setIsLoading(true);
    try {
      await adminApi.put('/admin/settings', settings);
      dispatch(showToast({ message: 'Settings saved successfully!', type: 'success' }));
    } catch (error) { dispatch(showToast({ message: 'Failed to save settings', type: 'error' })); }
    finally { setIsLoading(false); }
  };

  const tabs = [
    { id: 'general', label: 'GENERAL', icon: 'fa-gear' },
    { id: 'trading', label: 'TRADING', icon: 'fa-chart-line' },
    { id: 'limits', label: 'LIMITS & FEES', icon: 'fa-scale-balanced' },
    { id: 'support', label: 'SUPPORT', icon: 'fa-headset' },
  ];

  return (
    <div>
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4 p-5 border-b-2 border-[#e2e8f0] bg-[#f8fafc]">
        <div>
          <h2 className="font-syne text-xl font-extrabold text-[#0b1e33] uppercase tracking-tight">PLATFORM SETTINGS</h2>
          <p className="text-[11px] text-[#475569] font-mono font-extrabold mt-1 uppercase tracking-wider">Configure trading platform parameters</p>
        </div>
        <button onClick={handleSave} disabled={isLoading}
          className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#1e3a5f] text-white font-syne text-xs font-extrabold uppercase tracking-wider transition-all hover:bg-[#0b1e33] hover:shadow-lg hover:shadow-[#1e3a5f]/30 disabled:opacity-50 border-b-[3px] border-[#0b1e33]">
          {isLoading ? (
            <><span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" /> SAVING…</>
          ) : (
            <><i className="fas fa-save mr-2" /> SAVE SETTINGS</>
          )}
        </button>
      </div>

      {/* Tabs */}
      <div className="flex border-b-2 border-[#e2e8f0] px-5 bg-white">
        {tabs.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-5 py-3.5 text-[11px] font-syne font-extrabold transition-all border-b-[3px] uppercase tracking-wider ${
              activeTab === tab.id
                ? 'text-[#1e3a5f] border-[#1e3a5f]'
                : 'text-[#94a3b8] border-transparent hover:text-[#475569]'
            }`}>
            <i className={`fas ${tab.icon} text-[10px]`} />{tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="p-6 bg-white">
        {/* General Settings */}
        {activeTab === 'general' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="text-[10px] text-[#1e3a5f] font-mono font-extrabold tracking-wider uppercase block mb-2">
                  <i className="fas fa-globe mr-1.5" /> SITE NAME
                </label>
                <input type="text" name="siteName" value={settings.siteName} onChange={handleChange}
                  className="w-full bg-[#f8fafc] border-2 border-[#cbd5e1] rounded-xl px-4 py-3 text-[#0f172a] text-sm font-extrabold outline-none focus:border-[#1e3a5f] focus:bg-white transition-all" />
              </div>
              <div>
                <label className="text-[10px] text-[#1e3a5f] font-mono font-extrabold tracking-wider uppercase block mb-2">
                  <i className="fas fa-code-branch mr-1.5" /> SITE VERSION
                </label>
                <input type="text" name="siteVersion" value={settings.siteVersion} onChange={handleChange}
                  className="w-full bg-[#f8fafc] border-2 border-[#cbd5e1] rounded-xl px-4 py-3 text-[#0f172a] text-sm font-extrabold outline-none focus:border-[#1e3a5f] focus:bg-white transition-all" />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-5 bg-[#f8fafc] border-2 border-[#e2e8f0] rounded-xl">
              <label className="flex items-center gap-3 cursor-pointer">
                <input type="checkbox" name="maintenanceMode" checked={settings.maintenanceMode} onChange={handleChange}
                  className="w-5 h-5 accent-[#dc2626] cursor-pointer" />
                <span className="text-[12px] text-[#0f172a] font-extrabold uppercase tracking-wider">MAINTENANCE MODE</span>
              </label>
              <label className="flex items-center gap-3 cursor-pointer">
                <input type="checkbox" name="registrationEnabled" checked={settings.registrationEnabled} onChange={handleChange}
                  className="w-5 h-5 accent-[#059669] cursor-pointer" />
                <span className="text-[12px] text-[#0f172a] font-extrabold uppercase tracking-wider">REGISTRATION ENABLED</span>
              </label>
            </div>

            {settings.maintenanceMode && (
              <div className="p-4 bg-[#fef2f2] border-[3px] border-[#dc2626] rounded-xl">
                <div className="flex items-center gap-3">
                  <i className="fas fa-exclamation-triangle text-[#dc2626] text-lg" />
                  <span className="text-[11px] text-[#dc2626] font-syne font-extrabold uppercase tracking-wider">Maintenance mode is ON. Users cannot access trading features.</span>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Trading Settings */}
        {activeTab === 'trading' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="text-[10px] text-[#1e3a5f] font-mono font-extrabold tracking-wider uppercase block mb-2">
                  <i className="fas fa-chart-simple mr-1.5" /> DEFAULT LEVERAGE (1:X)
                </label>
                <select name="defaultLeverage" value={settings.defaultLeverage} onChange={handleChange}
                  className="w-full bg-[#f8fafc] border-2 border-[#cbd5e1] rounded-xl px-4 py-3 text-[#0f172a] text-sm font-extrabold outline-none focus:border-[#1e3a5f] focus:bg-white transition-all cursor-pointer">
                  <option value={10}>1:10</option><option value={20}>1:20</option><option value={50}>1:50</option>
                  <option value={100}>1:100</option><option value={200}>1:200</option><option value={500}>1:500</option>
                </select>
              </div>
              <div>
                <label className="text-[10px] text-[#1e3a5f] font-mono font-extrabold tracking-wider uppercase block mb-2">
                  <i className="fas fa-chart-simple mr-1.5" /> MAXIMUM LEVERAGE (1:X)
                </label>
                <select name="maxLeverage" value={settings.maxLeverage} onChange={handleChange}
                  className="w-full bg-[#f8fafc] border-2 border-[#cbd5e1] rounded-xl px-4 py-3 text-[#0f172a] text-sm font-extrabold outline-none focus:border-[#1e3a5f] focus:bg-white transition-all cursor-pointer">
                  <option value={50}>1:50</option><option value={100}>1:100</option>
                  <option value={200}>1:200</option><option value={500}>1:500</option>
                </select>
              </div>
            </div>

            <div className="p-4 bg-[#eff6ff] border-2 border-[#bfdbfe] rounded-xl">
              <div className="text-[11px] text-[#475569] font-bold">
                <i className="fas fa-info-circle text-[#1e3a5f] mr-2" />
                Higher leverage increases potential profits but also increases risk. Users can select lower leverage levels individually.
              </div>
            </div>
          </div>
        )}

        {/* Limits & Fees */}
        {activeTab === 'limits' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[
                { label: 'MINIMUM DEPOSIT ($)', name: 'minDeposit', icon: 'fa-circle-plus', min: '10' },
                { label: 'MAXIMUM DEPOSIT ($)', name: 'maxDeposit', icon: 'fa-circle-plus', min: '100' },
                { label: 'MINIMUM WITHDRAWAL ($)', name: 'minWithdrawal', icon: 'fa-circle-minus', min: '10' },
                { label: 'MAX WITHDRAWAL/DAY ($)', name: 'maxWithdrawalPerDay', icon: 'fa-circle-minus', min: '100' },
                { label: 'TRADING FEE (%)', name: 'tradingFee', icon: 'fa-percent', min: '0', step: '0.01' },
                { label: 'WITHDRAWAL FEE ($)', name: 'withdrawalFee', icon: 'fa-dollar-sign', min: '0', step: '0.01' },
              ].map(field => (
                <div key={field.name}>
                  <label className="text-[10px] text-[#1e3a5f] font-mono font-extrabold tracking-wider uppercase block mb-2">
                    <i className={`fas ${field.icon} mr-1.5`} /> {field.label}
                  </label>
                  <input type="number" name={field.name} value={settings[field.name]} onChange={handleChange}
                    min={field.min} step={field.step || '1'}
                    className="w-full bg-[#f8fafc] border-2 border-[#cbd5e1] rounded-xl px-4 py-3 text-[#0f172a] text-sm font-extrabold outline-none focus:border-[#1e3a5f] focus:bg-white transition-all" />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Support Settings */}
        {activeTab === 'support' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="text-[10px] text-[#1e3a5f] font-mono font-extrabold tracking-wider uppercase block mb-2">
                  <i className="fas fa-envelope mr-1.5" /> SUPPORT EMAIL
                </label>
                <input type="email" name="supportEmail" value={settings.supportEmail} onChange={handleChange}
                  className="w-full bg-[#f8fafc] border-2 border-[#cbd5e1] rounded-xl px-4 py-3 text-[#0f172a] text-sm font-extrabold outline-none focus:border-[#1e3a5f] focus:bg-white transition-all" />
              </div>
              <div>
                <label className="text-[10px] text-[#1e3a5f] font-mono font-extrabold tracking-wider uppercase block mb-2">
                  <i className="fas fa-phone mr-1.5" /> SUPPORT PHONE
                </label>
                <input type="tel" name="supportPhone" value={settings.supportPhone} onChange={handleChange}
                  className="w-full bg-[#f8fafc] border-2 border-[#cbd5e1] rounded-xl px-4 py-3 text-[#0f172a] text-sm font-extrabold outline-none focus:border-[#1e3a5f] focus:bg-white transition-all" />
              </div>
            </div>

            <div className="p-5 bg-[#ecfdf5] border-2 border-[#a7f3d0] rounded-xl">
              <div className="flex items-start gap-3">
                <i className="fas fa-envelope text-[#059669] text-lg mt-0.5" />
                <div>
                  <div className="text-[12px] text-[#0f172a] font-extrabold mb-1.5 uppercase tracking-wider">SUPPORT TICKET SYSTEM</div>
                  <div className="text-[10px] text-[#475569] font-bold">Customer support tickets are automatically routed to this email address. Response time is typically under 24 hours.</div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Danger Zone */}
      <div className="border-t-2 border-[#e2e8f0] p-5 bg-[#fef2f2]">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h4 className="text-sm font-extrabold text-[#dc2626] uppercase tracking-wider mb-1 flex items-center gap-2">
              <i className="fas fa-triangle-exclamation" /> DANGER ZONE
            </h4>
            <p className="text-[10px] text-[#991b1b] font-mono font-bold uppercase tracking-wider">Reset platform settings to default values</p>
          </div>
          <button
            onClick={() => {
              if (window.confirm('Are you sure you want to reset all settings to default?')) {
                dispatch(showToast({ message: 'Settings reset to default', type: 'success' }));
              }
            }}
            className="px-5 py-2.5 rounded-xl bg-[#dc2626] text-white text-xs font-syne font-extrabold uppercase tracking-wider transition-all hover:bg-[#b91c1c] hover:shadow-lg border-b-[3px] border-[#b91c1c]">
            <i className="fas fa-undo-alt mr-1.5" /> RESET TO DEFAULT
          </button>
        </div>
      </div>
    </div>
  );
};

export default Settings;